// ═══════════════════════════════════════════════════════════════
// useResolverNombres — Hooks para resolver UIDs/empresaIds → nombres
// ═══════════════════════════════════════════════════════════════
//
// Patrón sistémico detectado en #75 y #33: muchas páginas muestran UIDs
// crudos (kryw8VmglNcSEMUSD..., 3b8Z5iKMNBGm...) en columnas como
// "Asignado", "Owner", "Autor". Mismo problema con empresaId que aparece
// como id en lugar del nombre legible.
//
// Estos hooks centralizan el resolver con:
//   - Cache de sesión (Map global, se vacía al logout).
//   - Chunks de 30 IDs por query (límite del operador 'in').
//   - Skip de IDs ya cacheados (evita refetch).
//   - Fallback al ID truncado si el doc no existe.
//
// Uso típico en una página:
//
//   const uids = useMemo(() => [
//     caso.owner, caso.asignadoA, ...caso.participantesUserIds,
//     ...feed.map(f => f.autor),
//   ].filter(Boolean), [caso, feed])
//   const { usuariosById } = useUsuariosByIds(uids)
//   // usuariosById es { uid: { displayName, nombre, email, ... } | null }
//   const nombreOwner = formatNombreUser(usuariosById[caso.owner])
//
// El helper `formatNombreUser` prioriza: displayName > nombre > email > uid corto.
// El helper `formatNombreEmpresa` prioriza: nombre > razonSocial > id corto.
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useMemo, useRef } from 'react'
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore'
import { db } from '@core/lib/firebase'
import { COLLECTIONS } from '@core/lib/constants'
import { log } from '@core/lib/logger'
import { docConId } from '@core/lib/docConId'

// ── Caches globales de sesión ────────────────────────────────────
const _usuariosCache = new Map()  // uid → { id, displayName, nombre, email, ... }
const _empresasCache = new Map()  // empresaId → { id, nombre, razonSocial, ... }
// Inflight tracking — evita pedir el mismo ID 2 veces en paralelo (componentes
// que montan al mismo tiempo).
const _usuariosInflight = new Map()  // uid → Promise
const _empresasInflight = new Map()  // empresaId → Promise

/** Vaciá los caches al logout (llamar desde AuthContext). */
export function limpiarCachesNombres() {
  _usuariosCache.clear()
  _empresasCache.clear()
  _usuariosInflight.clear()
  _empresasInflight.clear()
}

// ── Helpers de formato ───────────────────────────────────────────

/**
 * Formato visible para un user. Prioridad: displayName > nombre > email > uid corto.
 * @param {object|null} user — doc del user, o null si no se cargó.
 * @param {string} [uidFallback] — para mostrar si no hay doc cargado todavía.
 */
export function formatNombreUser(user, uidFallback = null) {
  if (!user) return uidFallback ? uidFallback.slice(0, 8) + '…' : '—'
  return user.displayName || user.nombre || user.email || (user.id?.slice(0, 8) + '…') || '—'
}

/** Formato visible para una empresa. Prioridad: nombre > razonSocial > id corto. */
export function formatNombreEmpresa(empresa, idFallback = null) {
  if (!empresa) return idFallback ? idFallback.slice(0, 8) + '…' : '—'
  return empresa.nombre || empresa.razonSocial || (empresa.id?.slice(0, 8) + '…') || '—'
}

// ── Fetch genérico con cache + chunks de 30 ─────────────────────

async function _fetchEnChunks(coleccion, ids, cache, inflight) {
  // Filtrar los que ya están en cache (no hace falta volver a pedirlos).
  const idsAPedir = ids.filter(id => id && !cache.has(id))
  if (idsAPedir.length === 0) return

  // De los faltantes, separar los que ya están inflight (otro componente
  // los pidió hace milisegundos) de los nuevos.
  const nuevos = []
  const promesasInflight = []
  for (const id of idsAPedir) {
    if (inflight.has(id)) {
      promesasInflight.push(inflight.get(id))
    } else {
      nuevos.push(id)
    }
  }

  // Pedir los nuevos en chunks de 30 (límite operador 'in').
  if (nuevos.length > 0) {
    const chunks = []
    for (let i = 0; i < nuevos.length; i += 30) chunks.push(nuevos.slice(i, i + 30))

    // Una promesa "raíz" por chunk; cada id se mapea a esa promesa en inflight
    // para que otros componentes esperen el mismo fetch.
    const chunkPromises = chunks.map(async (c) => {
      try {
        const snap = await getDocs(query(collection(db, coleccion), where('__name__', 'in', c)))
        snap.docs.forEach(d => cache.set(d.id, docConId(d)))
        // Los IDs que no vinieron en el snap (no existen) los marcamos como null
        // para no quedar refetcheando para siempre.
        for (const id of c) {
          if (!cache.has(id)) cache.set(id, null)
        }
      } catch (e) {
        // El `in` es una query de LISTA: las rules la evalúan sin `resource.data`,
        // así que un solo id que no pase tumba el chunk entero y antes eso
        // marcaba null a los 30 — cero nombres justo donde más se notan (el
        // emisor staff de un escalamiento, report eOV7sp1SqK0rq2iLfSh6).
        // Reintento POR DOCUMENTO: `get` sí evalúa `resource.data`, así que
        // cada id degrada solo y los que se pueden leer se leen.
        log.warn('resolverNombres: lista denegada, reintento por documento', { coleccion, code: e?.code })
        const puntuales = await Promise.allSettled(c.map(id => getDoc(doc(db, coleccion, id))))
        puntuales.forEach((r, i) => {
          const id = c[i]
          if (cache.has(id)) return
          cache.set(id, r.status === 'fulfilled' && r.value.exists() ? { ...r.value.data(), id } : null)
        })
      }
    })

    // Registrar cada id como inflight con su promesa.
    chunks.forEach((c, i) => {
      for (const id of c) inflight.set(id, chunkPromises[i])
    })

    await Promise.all(chunkPromises)

    // Limpiar inflight.
    for (const id of nuevos) inflight.delete(id)
  }

  // Esperar las promesas inflight que ya estaban corriendo (si las hay).
  if (promesasInflight.length > 0) {
    await Promise.allSettled(promesasInflight)
  }
}

// ── Hooks React ──────────────────────────────────────────────────

/**
 * Hook: resuelve un set de UIDs a docs de /usuarios.
 * Re-fetch automático cuando cambia el set (con cache, no piensa duplicados).
 *
 * @param {string[]} uids — array de UIDs (puede tener duplicados/null).
 * @returns {{ usuariosById: object, loading: boolean }}
 *   usuariosById = { [uid]: doc | null }. null = no existe.
 */
export function useUsuariosByIds(uids) {
  // Dedupe + remover falsy.
  const uidsLimpios = useMemo(
    () => Array.from(new Set((uids || []).filter(Boolean))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify((uids || []).filter(Boolean).sort())]
  )

  const [version, setVersion] = useState(0)
  const [loading, setLoading] = useState(false)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  useEffect(() => {
    if (uidsLimpios.length === 0) return
    let cancel = false
    setLoading(true)
    _fetchEnChunks(COLLECTIONS.USUARIOS, uidsLimpios, _usuariosCache, _usuariosInflight)
      .then(() => {
        if (cancel || !mountedRef.current) return
        setVersion(v => v + 1)
        setLoading(false)
      })
    return () => { cancel = true }
  }, [uidsLimpios])

  // Armar el map de salida desde el cache.
  const usuariosById = useMemo(() => {
    const out = {}
    for (const uid of uidsLimpios) {
      out[uid] = _usuariosCache.get(uid) || null
    }
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uidsLimpios, version])

  return { usuariosById, loading }
}

/**
 * Hook: resuelve un set de empresaIds a docs de /empresas.
 *
 * @param {string[]} empresaIds — array de empresa IDs.
 * @returns {{ empresasById: object, loading: boolean }}
 */
export function useEmpresasByIds(empresaIds) {
  const idsLimpios = useMemo(
    () => Array.from(new Set((empresaIds || []).filter(Boolean))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify((empresaIds || []).filter(Boolean).sort())]
  )

  const [version, setVersion] = useState(0)
  const [loading, setLoading] = useState(false)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  useEffect(() => {
    if (idsLimpios.length === 0) return
    let cancel = false
    setLoading(true)
    _fetchEnChunks(COLLECTIONS.EMPRESAS, idsLimpios, _empresasCache, _empresasInflight)
      .then(() => {
        if (cancel || !mountedRef.current) return
        setVersion(v => v + 1)
        setLoading(false)
      })
    return () => { cancel = true }
  }, [idsLimpios])

  const empresasById = useMemo(() => {
    const out = {}
    for (const id of idsLimpios) {
      out[id] = _empresasCache.get(id) || null
    }
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsLimpios, version])

  return { empresasById, loading }
}
